import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, ExternalLink, Search } from "lucide-react";
import { listMyScoresheets } from "@/lib/api/parent-portal.functions";
import { LoadErrorState } from "@/components/parent/LoadErrorState";
import { describeLoadError } from "@/lib/browser-storage";

export const Route = createFileRoute("/_authenticated/responsavel/sumulas")({
  head: () => ({
    meta: [
      { title: "Súmulas | FutBux" },
      { name: "description", content: "Súmulas dos jogos em que seus alunos participaram." },
      { property: "og:title", content: "Súmulas | FutBux" },
      { property: "og:description", content: "Súmulas dos jogos em que seus alunos participaram." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  const listFn = useServerFn(listMyScoresheets);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [studentId, setStudentId] = useState<string>("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listFn();
      setRows(r.scoresheets ?? []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const students = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((s) => (s.my_students ?? []).forEach((st: any) => st?.id && map.set(st.id, st.name ?? "?")));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => rows.filter((s) => {
    if (studentId !== "all" && !(s.my_students ?? []).some((st: any) => st?.id === studentId)) return false;
    if (q) {
      const hay = `${s.championships?.name ?? ""} ${s.game_summons?.opponent ?? ""} ${s.visitor_team_name ?? ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, studentId, q]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;

  if (error) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <LoadErrorState
          rawError={error}
          message={describeLoadError(error, "Erro ao carregar as súmulas.")}
          onRetry={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Súmulas</h1>
        <p className="text-muted-foreground text-sm">Jogos em que seus alunos participaram · {rows.length} registro(s).</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="border rounded-md h-9 px-2 text-sm bg-background"
          value={studentId} onChange={(e) => setStudentId(e.target.value)}>
          <option value="all">Todos alunos</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-9" placeholder="Buscar por adversário / campeonato…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="p-6 text-sm text-muted-foreground">Nenhuma súmula encontrada.</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((s) => {
            const g = s.game_summons ?? {};
            const publicUrl = s.edit_token ? `/sumula/${s.id}?token=${s.edit_token}` : null;
            return (
              <Card key={s.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" /> {s.championships?.name ?? "Jogo"}
                    </span>
                    {s.event_categories?.category && <Badge variant="secondary">{s.event_categories.category}</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <div><b>{g.opponent ?? s.visitor_team_name ?? "-"}</b></div>
                  <div className="text-muted-foreground">
                    {g.match_date
                      ? new Date(`${g.match_date}T${g.match_time ?? "00:00"}`).toLocaleString("pt-BR")
                      : new Date(s.created_at).toLocaleString("pt-BR")}
                  </div>
                  {g.location && <div className="text-muted-foreground">📍 {g.location}</div>}
                  {s.my_students?.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Aluno(s): {s.my_students.map((st: any) => st?.name).filter(Boolean).join(", ")}
                    </div>
                  )}
                  {publicUrl && (
                    <div className="pt-2">
                      <Button asChild size="sm" variant="outline">
                        <a href={publicUrl} target="_blank" rel="noreferrer">
                          <ExternalLink className="mr-1 h-3 w-3" /> Abrir súmula
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
